import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Heart, 
  HandHeart, 
  Stethoscope, 
  Users, 
  CheckCircle2, 
  ArrowRight,
  ShieldCheck,
  Camera,
  Home
} from 'lucide-react';

// Cute puppy image
const PUPPY_IMAGE = "https://images.unsplash.com/photo-1680096212091-1f686b00081f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxhZG9yYWJsZSUyMHB1cHB5JTIwaGFwcHl8ZW58MHx8fHwxNzc2Nzk5MTQxfDA&ixlib=rb-4.1.0&q=85";
const PUPPIES_GROUP = "https://images.unsplash.com/photo-1680096211577-b26805661486?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwzfHxhZG9yYWJsZSUyMHB1cHB5JTIwaGFwcHl8ZW58MHx8fHwxNzc2Nzk5MTQxfDA&ixlib=rb-4.1.0&q=85";

// Navigation for Impact Page
const ImpactNavigation = () => {
  const [isScrolled, setIsScrolled] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md py-3' : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between">
        <Link to="/">
          <img 
            src={isScrolled 
              ? "https://customer-assets.emergentagent.com/job_a496982b-fa41-42f5-9942-207e322f4873/artifacts/78ul7ouf_Scalient%281%29.png"
              : "https://customer-assets.emergentagent.com/job_scalient-growth/artifacts/kshyqe7b_Scalient%20New.png"
            }
            alt="Scalient - Marketing That Matters"
            className={`h-12 md:h-14 w-auto transition-all duration-300 ${!isScrolled ? 'rounded-lg' : ''}`}
          />
        </Link>
        
        <div className="flex items-center gap-6">
          <Link 
            to="/"
            className={`text-sm font-medium transition-colors flex items-center gap-2 ${
              isScrolled ? 'text-gray-700 hover:text-rose-500' : 'text-white/90 hover:text-white'
            }`}
          >
            <Home size={16} />
            Back to Home
          </Link>
          <Button 
            onClick={() => window.location.href = '/#contact'}
            className="bg-rose-500 hover:bg-rose-600 text-white px-5 py-2 rounded-md font-semibold text-sm"
          >
            Get in Touch
          </Button>
        </div>
      </div>
    </nav>
  );
};

// Hero Section
const ImpactHero = () => (
  <section className="relative min-h-[70vh] flex items-center bg-gradient-to-br from-rose-800 via-rose-700 to-pink-800 pt-20">
    <div className="absolute inset-0 bg-black/20"></div>
    <div className="absolute inset-0 opacity-20">
      <div className="absolute top-20 left-10 w-64 h-64 bg-rose-400 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-80 h-80 bg-pink-400 rounded-full blur-3xl"></div>
    </div>
    
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 relative z-10">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
            <Heart className="text-rose-300" size={18} fill="currentColor" />
            <span className="text-rose-200 font-medium text-sm">Our Social Responsibility</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6">
            Business With Purpose
          </h1>
          
          <p className="text-xl md:text-2xl text-rose-100 mb-8 leading-relaxed">
            Every project you do with us helps feed and treat stray dogs in Chandigarh.
          </p>
          
          <div className="flex items-center gap-4">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-6 py-4">
              <p className="text-rose-300 font-bold text-3xl">50%</p>
              <p className="text-white/80 text-sm">of commissions donated</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-6 py-4">
              <p className="text-rose-300 font-bold text-3xl">Chandigarh</p>
              <p className="text-white/80 text-sm">focused impact</p>
            </div>
          </div>
        </div>
        
        <div className="hidden lg:block">
          <div className="relative">
            <div className="absolute -inset-4 bg-rose-400/20 rounded-2xl blur-xl"></div>
            <img 
              src={PUPPY_IMAGE}
              alt="Adorable stray puppy"
              className="relative rounded-2xl shadow-2xl w-full h-80 object-cover border-4 border-white/20"
            />
            <div className="absolute -bottom-4 -right-4 bg-white rounded-lg px-4 py-3 shadow-lg">
              <p className="text-rose-600 font-bold text-sm flex items-center gap-2">
                <Heart size={16} fill="currentColor" />
                Saving lives together
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Our Commitment Section
const CommitmentSection = () => (
  <section className="py-24 bg-white">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <p className="text-rose-500 font-semibold text-sm tracking-wide mb-4">OUR COMMITMENT</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            Transparent Giving, Real Impact
          </h2>
          <p className="text-gray-600 text-lg mb-6 leading-relaxed">
            At Scalient, we believe that success should be shared. That's why we've made a firm commitment: 
            <strong className="text-gray-900"> 50% of every commission we earn goes directly toward feeding and providing medical care for stray dogs in Chandigarh.</strong>
          </p>
          <p className="text-gray-600 text-lg mb-8 leading-relaxed">
            This isn't a one-time gesture or a marketing campaign—it's a core part of who we are. 
            Every month, consistently and transparently, we channel half of what we earn into making a difference.
          </p>
          
          <div className="flex flex-wrap gap-4">
            {['Consistent Monthly Donations', 'Full Transparency', 'Local Impact'].map((item, index) => (
              <div key={index} className="flex items-center gap-2 bg-rose-50 px-4 py-2 rounded-full">
                <CheckCircle2 className="text-rose-500" size={18} />
                <span className="text-gray-700 font-medium text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>
        
        <div className="relative">
          <div className="bg-gradient-to-br from-rose-100 to-pink-100 rounded-2xl p-8">
            <img 
              src={PUPPIES_GROUP}
              alt="Stray puppies we help"
              className="rounded-xl w-full h-64 object-cover mb-6"
            />
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <HandHeart className="text-rose-500 mb-3" size={36} />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Our Promise</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                For every project completed, every campaign launched—half of our earnings 
                go straight to helping those who can't help themselves.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Why It Matters Section
const WhyItMattersSection = () => (
  <section className="py-24 bg-stone-50">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="text-center mb-16">
        <p className="text-rose-500 font-semibold text-sm tracking-wide mb-4">WHY IT MATTERS</p>
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
          The Reality on Our Streets
        </h2>
        <p className="text-gray-600 text-lg max-w-2xl mx-auto">
          Understanding the challenges stray animals face helps us appreciate the importance of consistent support.
        </p>
      </div>
      
      <div className="grid md:grid-cols-3 gap-8">
        {[
          {
            title: 'Hunger & Malnutrition',
            description: 'Thousands of stray dogs in urban areas like Chandigarh struggle daily to find food. Many go days without a proper meal, leading to weakness and vulnerability.',
            icon: '🍽️'
          },
          {
            title: 'Lack of Medical Care',
            description: 'Without access to veterinary services, minor injuries become life-threatening. Infections, skin diseases, and treatable conditions often go unaddressed.',
            icon: '🏥'
          },
          {
            title: 'Accidents & Injuries',
            description: 'Busy roads and urban hazards result in frequent injuries. Without intervention, these animals suffer in silence, often with no hope of recovery.',
            icon: '🚗'
          }
        ].map((item, index) => (
          <div key={index} className="bg-white rounded-xl p-8 border border-stone-200">
            <span className="text-4xl mb-4 block">{item.icon}</span>
            <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
            <p className="text-gray-600 leading-relaxed">{item.description}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// How We Help Section
const HowWeHelpSection = () => (
  <section className="py-24 bg-white">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <div className="order-2 lg:order-1">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-rose-500 rounded-xl p-6 text-white">
              <Stethoscope className="mb-3" size={32} />
              <h4 className="font-bold text-lg mb-1">Medical Care</h4>
              <p className="text-rose-100 text-sm">Emergency treatments & vaccinations</p>
            </div>
            <div className="bg-pink-500 rounded-xl p-6 text-white">
              <Heart className="mb-3" size={32} />
              <h4 className="font-bold text-lg mb-1">Daily Feeding</h4>
              <p className="text-pink-100 text-sm">Nutritious meals every day</p>
            </div>
            <div className="bg-stone-700 rounded-xl p-6 text-white">
              <Users className="mb-3" size={32} />
              <h4 className="font-bold text-lg mb-1">Caregiver Support</h4>
              <p className="text-stone-300 text-sm">Empowering local heroes</p>
            </div>
            <div className="bg-rose-600 rounded-xl p-6 text-white">
              <Home className="mb-3" size={32} />
              <h4 className="font-bold text-lg mb-1">Shelter Aid</h4>
              <p className="text-rose-100 text-sm">Supporting local shelters</p>
            </div>
          </div>
        </div>
        
        <div className="order-1 lg:order-2">
          <p className="text-rose-500 font-semibold text-sm tracking-wide mb-4">HOW WE HELP</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            Making a Difference in Chandigarh
          </h2>
          <p className="text-gray-600 text-lg mb-8 leading-relaxed">
            Our contributions are focused entirely in Chandigarh, ensuring that the impact is local, measurable, and meaningful.
          </p>
          
          <ul className="space-y-4">
            {[
              'Daily feeding initiatives across multiple locations',
              'Medical treatments including surgeries and emergency care',
              'Vaccination and sterilization programs',
              'Support for dedicated local caregivers and volunteers',
              'Collaboration with animal shelters and rescue organizations'
            ].map((item, index) => (
              <li key={index} className="flex items-start gap-3">
                <CheckCircle2 className="text-rose-500 flex-shrink-0 mt-1" size={20} />
                <span className="text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

// Your Impact Section
const YourImpactSection = () => (
  <section className="py-24 bg-gradient-to-br from-rose-500 to-pink-500 text-white">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="text-center max-w-3xl mx-auto">
        <p className="text-rose-100 font-semibold text-sm tracking-wide mb-4">YOUR IMPACT</p>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
          Every Client Makes a Difference
        </h2>
        <p className="text-xl text-rose-50 mb-8 leading-relaxed">
          When you work with Scalient, you're not just building your business—you're helping save lives. 
          Every campaign we run, every goal we achieve together, directly contributes to the welfare of stray dogs in Chandigarh.
        </p>
        
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
          <p className="text-2xl font-semibold italic">
            "Your project doesn't just drive results for your brand. It provides meals, medicine, and hope for animals in need."
          </p>
        </div>
      </div>
    </div>
  </section>
);

// Transparency Section
const TransparencySection = () => (
  <section className="py-24 bg-stone-50">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <p className="text-rose-500 font-semibold text-sm tracking-wide mb-4">TRANSPARENCY & TRUST</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            Honest, Accountable, Open
          </h2>
          <p className="text-gray-600 text-lg mb-6 leading-relaxed">
            We believe in complete transparency. Our commitment to this cause isn't just a promise—it's a practice we uphold with accountability.
          </p>
          <p className="text-gray-600 text-lg mb-8 leading-relaxed">
            We're happy to share updates, photos, and reports on how contributions are being used. Because when you trust us with your business, we want you to trust us with our purpose too.
          </p>
          
          <div className="flex flex-wrap gap-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-rose-100 rounded-lg flex items-center justify-center">
                <ShieldCheck className="text-rose-500" size={24} />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Verified Donations</p>
                <p className="text-gray-500 text-sm">Tracked and documented</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-rose-100 rounded-lg flex items-center justify-center">
                <Camera className="text-rose-500" size={24} />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Regular Updates</p>
                <p className="text-gray-500 text-sm">Photos and reports shared</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl p-8 border border-stone-200 shadow-sm">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Our Transparency Promise</h3>
          <ul className="space-y-4">
            {[
              'Monthly donation receipts available on request',
              'Photo updates from feeding and medical camps',
              'Direct communication with partner organizations',
              'Annual impact summary shared with clients'
            ].map((item, index) => (
              <li key={index} className="flex items-start gap-3">
                <CheckCircle2 className="text-green-500 flex-shrink-0 mt-0.5" size={20} />
                <span className="text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

// CTA Section
const CTASection = () => (
  <section className="py-24 bg-gray-900">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
        Work With Us. Make a Difference.
      </h2>
      <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-10">
        Partner with an agency that delivers results for your business while creating positive change in the world.
      </p>
      
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Button 
          onClick={() => window.location.href = '/#contact'}
          className="bg-rose-500 hover:bg-rose-600 text-white px-8 py-4 rounded-md font-bold text-base"
        >
          Start a Project
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
        <Button 
          onClick={() => window.location.href = 'tel:+917837064744'}
          className="bg-transparent border border-white/30 hover:bg-white/10 text-white px-8 py-4 rounded-md font-semibold text-base"
        >
          Get in Touch
        </Button>
      </div>
    </div>
  </section>
);

// Footer for Impact Page
const ImpactFooter = () => (
  <footer className="bg-gray-900 text-gray-400 py-12 border-t border-gray-800">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="flex flex-col md:flex-row justify-between items-center gap-6">
        <div className="flex items-center gap-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_scalient-growth/artifacts/kshyqe7b_Scalient%20New.png"
            alt="Scalient"
            className="h-10 w-auto rounded"
          />
          <span className="text-white font-medium">Marketing That Matters</span>
        </div>
        
        <div className="flex gap-6 text-sm">
          <Link to="/" className="hover:text-rose-400 transition-colors">Home</Link>
          <Link to="/our-impact" className="hover:text-rose-400 transition-colors">Our Impact</Link>
          <a href="/#contact" className="hover:text-rose-400 transition-colors">Contact</a>
        </div>
        
        <p className="text-sm">
          © {new Date().getFullYear()} Scalient. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);

// Main Our Impact Page
const OurImpact = () => {
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen">
      <ImpactNavigation />
      <main>
        <ImpactHero />
        <CommitmentSection />
        <WhyItMattersSection />
        <HowWeHelpSection />
        <YourImpactSection />
        <TransparencySection />
        <CTASection />
      </main>
      <ImpactFooter />
    </div>
  );
};

export default OurImpact;
